import { ICartAddEvent } from "../../Interfaces/CartFlow";
import { ICartItemProperties, ICartItems } from "../../Interfaces/Cart";
import { Success } from "./Flow/Product/Success";
import { Success as RentalSuccess } from "./Flow/Rental/Success";
import { Success as EbikeSuccess } from "./Flow/Ebike/Success";
import { Success as EbikeRentalSuccess } from "./Flow/EbikeRental/Success";
import { Shipping } from "./Flow/Product/Shipping";
import { Register } from "./Flow/Product/Register";
import { Guarantee } from "./Flow/Product/Guarantee";
import { Guarantee as EbikeGuarantee } from "./Flow/Ebike/Guarantee";
import { Choice } from "./Flow/Choice";
import cartManager from "./CartManager";
import { Insurance } from "./Flow/Rental/Insurance";
import { Accessories } from "./Flow/Rental/Accessories";
import { BUYING_TYPES, BUYING_TYPES_EBIKE, RENTAL_TYPES, RENTAL_TYPES_EBIKE, SELECTORS } from "./Constants";
import { CartItem } from "./CartItem";
import { additionalCartItemProperties } from "./Helper/CartItemProperties";
import { Quantity } from "../../Components/Form/Quantity";

class CartFlowController {
    static EVENTS = {
        ADD_SUCCESS: "cart-add-success",
        CONFIGURE_SHIPPING: "cart-flow-shipping",
        CONFIGURE_REGISTER: "cart-flow-register",
        CONFIGURE_GUARANTEE: "cart-flow-guarantee",
        CONFIGURE_DONE: "cart-flow-done",
        CONFIGURE_INSURANCE: "rental-flow-insurance",
        RENTAL_ADD_SUCCESS: "rental-add-success",
        RENTAL_DONE: "rental-done",
        EBIKE_ADD_SUCCESS: "ebike-add-success",
        EBIKE_DONE: "ebike-done",
        EBIKE_RENTAL_ADD_SUCCESS: "ebike-rental-add-success",
        EBIKE_RENTAL_DONE: "ebike-rental-done"
    };

    private cartItems: ICartItems;

    private choices: { [key: string]: Choice | Insurance } = {};

    private static readonly BUYING_TYPES = BUYING_TYPES; // TODO where to get from?
    private static readonly RENTAL_TYPES = RENTAL_TYPES;
    private static readonly BUYING_TYPES_EBIKE = BUYING_TYPES_EBIKE;
    private static readonly RENTAL_TYPES_EBIKE = RENTAL_TYPES_EBIKE;

    constructor() {
        this.addListeners();
    }

    /**
     * add event listeners
     */
    addListeners(): void {
        window.addEventListener(CartFlowController.EVENTS.ADD_SUCCESS, this.handleAddSuccess.bind(this));
        window.addEventListener(CartFlowController.EVENTS.CONFIGURE_SHIPPING, this.handleConfigureShipping.bind(this));
        window.addEventListener(
            CartFlowController.EVENTS.CONFIGURE_REGISTER,
            this.handleConfigureRegistration.bind(this)
        );
        window.addEventListener(
            CartFlowController.EVENTS.CONFIGURE_GUARANTEE,
            this.handleConfigureGuarantee.bind(this)
        );
        window.addEventListener(CartFlowController.EVENTS.CONFIGURE_DONE, this.handleConfigureDone.bind(this));
        window.addEventListener(CartFlowController.EVENTS.RENTAL_ADD_SUCCESS, this.handleRentalAddSuccess.bind(this));
        window.addEventListener(
            CartFlowController.EVENTS.CONFIGURE_INSURANCE,
            this.handleConfigureInsurance.bind(this)
        );
        window.addEventListener(CartFlowController.EVENTS.RENTAL_DONE, this.handleConfigureDone.bind(this));
        window.addEventListener(
            CartFlowController.EVENTS.EBIKE_ADD_SUCCESS,
            this.handleConfigureGuaranteeEbike.bind(this)
        );
        window.addEventListener(
            CartFlowController.EVENTS.EBIKE_RENTAL_ADD_SUCCESS,
            this.handleEbikeRentalAddSuccess.bind(this)
        );
        window.addEventListener(CartFlowController.EVENTS.EBIKE_RENTAL_DONE, this.handleConfigureDone.bind(this));
    }

    /**
     * handle cart add success
     * @param e
     */
    handleAddSuccess(e: ICartAddEvent): void {
        const productType = e.detail.items?.[0]?.product_type;
        this.cartItems = e.detail;
        if (CartFlowController.BUYING_TYPES.indexOf(productType) > -1) {
            new Success(this.cartItems).render();
        } else if (CartFlowController.RENTAL_TYPES.indexOf(productType) > -1) {
            new RentalSuccess(this.cartItems).render();
        } else if (CartFlowController.BUYING_TYPES_EBIKE.indexOf(productType) > -1) {
            new EbikeSuccess(this.cartItems).render();
        } else if (CartFlowController.RENTAL_TYPES_EBIKE.indexOf(productType) > -1) {
            new EbikeRentalSuccess(this.cartItems).render();
        }
    }

    handleRentalAddSuccess(): void {
        this.choices.insurance = new Insurance(this.cartItems);
        this.choices.insurance.render();
    }

    handleEbikeRentalAddSuccess(): void {
        this.choices.accessories = new Accessories(this.cartItems);
        this.choices.accessories.render();
    }

    /**
     * handle configure shipping
     */
    handleConfigureShipping(): void {
        this.choices.shipping = new Shipping(this.cartItems);
        this.choices.shipping.render();
    }

    /**
     * handle configure register
     */
    handleConfigureRegistration(): void {
        this.choices.registration = new Register(this.cartItems);
        this.choices.registration.render();
    }

    /**
     * handle configure guarantee
     */
    handleConfigureGuarantee(): void {
        this.choices.guarantee = new Guarantee(this.cartItems);
        this.choices.guarantee.render();
    }

    handleConfigureGuaranteeEbike(): void {
        this.choices.guarantee = new EbikeGuarantee(this.cartItems);
        this.choices.guarantee.render();
    }

    handleConfigureInsurance(): void {
        this.choices.accessories = new Accessories(this.cartItems);
        this.choices.accessories.render();
    }

    /**
     * handle configure done
     */
    async handleConfigureDone() {
        await cartManager.updateServiceProperties(
            this.cartItems.items[0].key,
            this.retrieveVariantIDsFromChoices(),
            this.retrieveServiceQuantities(),
            this.retrieveAdditionalProperties()
        );
        location.href = `${window.Shopify.routes.root}cart`;
    }

    private retrieveServiceQuantities(): { id: string; quantity: number }[] {
        const serviceQuantities: { id: string; quantity: number }[] = [];
        for (const key of Object.keys(this.choices)) {
            for (const id of this.choices[key].choice) {
                // For E-Bikes or potentially any other product there could be a quantity selector in the choice element
                const userSelectedQuantity = this.choices[key].content.querySelector<Quantity>("x-quantity")?.value;

                let quantity = this.cartItems.items[0].quantity;
                if (userSelectedQuantity) {
                    quantity = Number(userSelectedQuantity);
                } else if (this.choices[key].quantity) {
                    quantity = this.choices[key].quantity;
                }

                serviceQuantities.push({
                    id: String(id),
                    quantity
                });
            }
        }
        return serviceQuantities;
    }

    private retrieveVariantIDsFromChoices(): string[] {
        let ids: string[] = [];
        for (const key of Object.keys(this.choices)) {
            ids = [...ids, ...this.choices[key].choice];
        }
        return ids;
    }

    private retrieveAdditionalProperties(): { [key: string]: ICartItemProperties } {
        return Object.keys(this.choices).reduce((acc, cur) => {
            const selector = `[${CartItem.DATA_ATTRIBUTES.CHOICE}]`;
            const activeChoices = [...this.choices[cur].content.querySelectorAll<HTMLElement>(selector)].filter((c) =>
                c.classList.contains(Choice.ACTIVE_CLASS)
            );
            return {
                ...acc,
                ...additionalCartItemProperties(activeChoices)
            };
        }, {});
    }
}

export { CartFlowController };

if (document.querySelector(SELECTORS.ADD)) {
    new CartFlowController();
}
